import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { CheckIcon, ChevronDown, RefreshCcwIcon, Search, X } from "lucide-react";
import { useUser } from "../../../../context/UserContext";
import { useHat } from "../../../../context/HatContext";
import { duplicateHatToUser, getMyDetails, getUsers } from "../../../../service/apiService";
import { User } from "../../../../types";

interface Hat {
  id?: string;
  hat_name: string;
  summary: string;
  directions: string;
  is_public: boolean;
  feature_keys: {
    image_creation: { enabled: boolean };
    internet_browsing: { enabled: boolean };
    kyva_llm: { enabled: boolean };
    kyva_connectors: { enabled: boolean };
    include_knowledge_base: {
      enabled: boolean;
      knowledge_base_ids: string[];
    };
  };
  favorites?: { [key: string]: boolean };
  selected_icon?: File | null;
  updated_at?: string;
  userId?: string;
  user_id?: string;
}

interface DuplicateVAFormProps {
  vaData: Hat | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const DuplicateVAForm: React.FC<DuplicateVAFormProps> = ({ vaData, onClose, onSuccess }) => {
  const { userId } = useUser();
  const { fetchHatsList } = useHat();
  
  // Form state
  const [newVAName, setNewVAName] = useState("");
  const [targetUser, setTargetUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [userQuery, setUserQuery] = useState("");
  
  const [nameError, setNameError] = useState<string | null>(null);
  
  // Check if current user has permission to duplicate this hat (matching edit logic)
const [canDuplicate, setCanDuplicate] = useState(false);
const [isCheckingPermission, setIsCheckingPermission] = useState(true); 

// Check permission on component mount
useEffect(() => {
  const checkDuplicationPermission = async () => {
    if (!vaData?.id) {
      setCanDuplicate(false);
      setIsCheckingPermission(false);
      return;
    }

    try {
      // Check basic permissions first
      const isOwner = vaData.userId === userId || vaData.user_id === userId;
      const isPublic = vaData.is_public;
      
      if (isOwner || isPublic) {
        setCanDuplicate(true);
        setIsCheckingPermission(false);
        return;
      }

      // Check if Hat is shared with user
      const userDetails = await getMyDetails();
      const sharedHatIds = userDetails.shared_hats || [];
      const isShared = sharedHatIds.includes(vaData.id);
      
      setCanDuplicate(isShared);
    } catch (error) {
      console.error("Error checking duplication permission:", error);
      setCanDuplicate(false);
    } finally {
      setIsCheckingPermission(false);
    }
  };

  checkDuplicationPermission();
}, [vaData, userId]);

  useEffect(() => {
    if (vaData) {
      setNewVAName(`${vaData.hat_name}`);
    }
    
    // Fetch users
    const fetchUsersList = async () => {
      setIsLoadingUsers(true);
      try {
        const result = await getUsers();
        setUsers(result);
        
        // Set current user as default target
        const currentUser = result.find(user => user.id === userId);
        if (currentUser) {
          setTargetUser(currentUser);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("Failed to load users");
      } finally {
        setIsLoadingUsers(false);
      }
    };
    
    fetchUsersList();
  }, [vaData, userId]);
  
  // Filtered users based on search
  const filteredUsers = userQuery === ""
    ? users
    : users.filter(user => 
        user.id.toLowerCase().includes(userQuery.toLowerCase())
      );
  
  // Validate the new VA name
  const validateName = (name: string): boolean => {
    if (!name.trim()) {
      setNameError("New name cannot be empty");
      return false;
    }
    
    if (vaData && name.trim().toLowerCase() === vaData.hat_name.toLowerCase()) {
      setNameError("New name must be different from the original");
      return false;
    }
    
    setNameError(null);
    return true;
  };
  
  // Handle name change
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setNewVAName(newName);
    validateName(newName);
  };
  
  // Handle user selection
  const handleUserSelect = (user: User) => {
    setTargetUser(user);
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!canDuplicate) {
      toast.error("You don't have permission to duplicate this Virtual Assistant");
      return;
    }
    
    if (!vaData?.id) {
      toast.error("No Virtual Assistant selected for duplication");
      return;
    }
    
    if (!targetUser) {
      toast.error("Please select a target user");
      return;
    }
    
    if (!validateName(newVAName)) {
      return;
    }
    
    setIsDuplicating(true);
    
    try {
      await duplicateHatToUser(vaData.id, targetUser.id, newVAName);
      toast.success("Virtual Assistant duplicated successfully");
      
      fetchHatsList();
      
      if (onSuccess) {
        onSuccess();
      }
      
      // Close the form
      onClose();
    } catch (error: any) {
      console.error("Error duplicating Virtual Assistant:", error);
      
      // Handle specific permission error
      if (error?.response?.data?.detail?.includes("don't have permission")) {
        toast.error("You don't have permission to duplicate this Virtual Assistant");
      } else {
        toast.error("Failed to duplicate Virtual Assistant");
      }
    } finally {
      setIsDuplicating(false);
    }
  };
  
  // Check if submit button should be disabled
  const isSubmitDisabled = !canDuplicate || isDuplicating || !targetUser || !!nameError || !newVAName.trim();
  // Show loading state while checking permissions
if (isCheckingPermission) {
  return (
    <div className="flex justify-center items-center py-8">
      <RefreshCcwIcon className="animate-spin h-6 w-6 text-gray-400" />
      <span className="ml-2 text-gray-600">Checking permissions...</span>
    </div>
  );
}
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Permission Warning */}
      {!canDuplicate && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Permission Required
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  You can duplicate Virtual Assistants that you own, that are shared with you, or that are public.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Original VA Name (Non-editable) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Virtual Assistant Name:
        </label>
        <input
          type="text"
          value={vaData?.hat_name || ""}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 cursor-not-allowed"
          disabled
        />
      </div>
      
      {/* New VA Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          New Virtual Assistant Name:
        </label>
        <input
          type="text"
          name="new_va_name"
          value={newVAName}
          onChange={handleNameChange}
          className={`w-full px-3 py-2 bg-white text-gray-900 border ${
            nameError ? "border-red-500" : "border-gray-300"
          } rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500 ${
            !canDuplicate ? "bg-gray-50 cursor-not-allowed" : ""
          }`}
          placeholder="Enter new name..."
          disabled={!canDuplicate}
          required
        />
        {nameError && (
          <p className="mt-1 text-xs text-red-600">{nameError}</p>
        )}
      </div>
      
      {/* Target User (using Share with individual users pattern) */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-sm font-medium text-gray-700">Target User:</h3>
        </div>
        <div className={`border border-gray-200 rounded-md p-4 h-48 overflow-y-auto ${
          !canDuplicate ? "bg-gray-50" : ""
        }`}>
          {/* Search input */}
          <div className="relative mb-3">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className={`w-full pl-9 pr-3 py-1.5 border border-gray-200 text-sm text-gray-700 bg-white rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500 ${
                !canDuplicate ? "bg-gray-50 cursor-not-allowed" : ""
              }`}
              placeholder="Search users..."
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              disabled={!canDuplicate}
            />
            {isLoadingUsers && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                <RefreshCcwIcon className="animate-spin h-4 w-4 text-gray-400" />
              </div>
            )}
          </div>
          
          {/* Selected user chips */}
          {targetUser && (
            <div className="flex flex-wrap gap-2 mb-2">
              <div 
                key={`selected-${targetUser.id}`}
                className="flex items-center bg-violet-50 text-violet-700 text-xs rounded-full px-2 py-1"
              >
                <span className="mr-1">{targetUser.id === userId ? `${targetUser.id} (You)` : targetUser.id}</span>
                {canDuplicate && (
                  <button
                    type="button"
                    onClick={() => setTargetUser(null)}
                    className="text-violet-500 hover:text-violet-700 focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          )}
          
          {/* Users list */}
          <div className="space-y-1 mt-2">
            {isLoadingUsers ? (
              <div className="flex justify-center py-2">
                <RefreshCcwIcon className="animate-spin h-5 w-5 text-gray-400" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-sm text-gray-500 text-center py-2">
                {userQuery ? "No users found" : "No users available"}
              </div>
            ) : (
              filteredUsers.map(user => {
                const isSelected = targetUser?.id === user.id;
                return (
                  <div 
                    key={user.id}
                    className={`flex items-center justify-between py-1.5 px-2 rounded ${
                      isSelected ? "bg-violet-50" : ""
                    } ${!canDuplicate ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                    onClick={() => canDuplicate && handleUserSelect(user)}
                  >
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`user-${user.id}`}
                        checked={isSelected}
                        onChange={() => {}}
                        disabled={!canDuplicate}
                        className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                      />
                      <label 
                        htmlFor={`user-${user.id}`}
                        className={`text-sm text-gray-700 ${canDuplicate ? "cursor-pointer" : "cursor-not-allowed"}`}
                      >
                        {user.id === userId ? `${user.id} (You)` : user.id}
                      </label>
                    </div>
                    {isSelected && (
                      <CheckIcon className="h-4 w-4 text-violet-600" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      
      {/* Summary (Non-editable) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Summary:
        </label>
        <textarea
          value={vaData?.summary || ""}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 cursor-not-allowed"
          disabled
        />
      </div>
      
      {/* Instructions (Non-editable) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Instructions:
        </label>
        <textarea
          value={vaData?.directions || ""}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 cursor-not-allowed"
          disabled
        />
      </div>
      
      {/* Features (Non-editable) */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-2">Features:</h3>
        <div className="grid grid-cols-2 gap-4">
          {/* placeholder - it is not developed yet */}
          {/* <div className="flex items-center">
            <input
              type="checkbox"
              checked={vaData?.feature_keys.image_creation.enabled || false}
              className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded cursor-not-allowed"
              disabled
            />
            <label className="ml-2 text-sm text-gray-700">
              Image Creation
            </label>
          </div> */}
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={vaData?.feature_keys.internet_browsing.enabled || false}
              className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded cursor-not-allowed"
              disabled
            />
            <label className="ml-2 text-sm text-gray-700">
              Internet Browsing
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={vaData?.feature_keys.kyva_llm.enabled || false}
              className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded cursor-not-allowed"
              disabled
            />
            <label className="ml-2 text-sm text-gray-700">
              Kyva LLM
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={vaData?.feature_keys.kyva_connectors.enabled || false}
              className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded cursor-not-allowed"
              disabled
            />
            <label className="ml-2 text-sm text-gray-700">
              Kyva Connectors
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={vaData?.is_public || false}
              className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded cursor-not-allowed"
              disabled
            />
            <label className="ml-2 text-sm text-gray-700">
              Make VA Available to Everyone
            </label>
          </div>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitDisabled}
          className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none ${
            isSubmitDisabled
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-violet-600 hover:bg-violet-700"
          }`}
          title={!canDuplicate ? "You don't have permission to duplicate this Virtual Assistant" : ""}
        >
          {isDuplicating ? (
            <span className="flex items-center">
              <RefreshCcwIcon className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" />
              Duplicating...
            </span>
          ) : (
            "Duplicate Virtual Assistant"
          )}
        </button>
      </div>
    </form>
  );
};

export default DuplicateVAForm;